/**
 * Darf der angemeldete Anwender Mitglieder **einladen** bzw. Konten
 * **aktivieren/deaktivieren**?
 *
 * 🔴 Serverseitig haengen `members.invite`, `members.activate` und
 * `members.deactivate` an der **Organisations-Rolle** (`requireRoleAdminRaw` →
 * owner/admin), nicht an einem Entitlement. Ein `usePermissions()`-Gate waere
 * deshalb ein Gate auf ein *anderes* Recht als das des Servers: entweder zu
 * streng (Flaeche unerreichbar) oder zu lax (403 nach dem Ausfuellen).
 *
 * ⚪ Die Schwester-Apps loesen den Wert in ihrem `OrganizationGuard` auf. Diese
 * App hat keinen — sie verlangt keine eingerichtete Organisation —, deshalb
 * steht die Ableitung hier als eigener Hook. Quelle und Rollenmenge sind
 * dieselben wie dort (`saas-dashandwerk-frontend`, `saas-verein-frontend`) und
 * wortgleich wie im Schwester-Fork `saas-invoice-api-frontend` (Commit
 * `901644c`) — App-Paritaet, kein zweiter Entwurf.
 */

import { trpc } from '@/trpc'

/**
 * Rollen, die als Organisations-Administration gelten. Better-Auth schreibt
 * 'owner' klein, aeltere Datensaetze fuehren 'Owner'/'Administrator' gross.
 */
const ADMIN_ROLES = new Set(['owner', 'Owner', 'admin', 'Admin', 'administrator', 'Administrator'])

/**
 * Hat der angemeldete Nutzer die Organisations-Rolle owner/admin?
 *
 * Waehrend die Abfrage laeuft: `false`. „Weiss nicht" muss hier „nein" heissen —
 * jeder Knopf dahinter endet in einer Mutation.
 */
function useIsOrgAdminRole(): boolean {
  const { data } = trpc.organization.current.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
  })
  const rolle = data?.userRole
  return rolle !== undefined && ADMIN_ROLES.has(rolle)
}

/**
 * Darf der angemeldete Nutzer Kollegen **einladen**? (`members.invite`)
 *
 * Ohne diesen Wert faellt `canInvite` auf seinen Default `false` — der
 * Einladen-Knopf verschwaende dann fuer ALLE, auch fuer den Owner. Genau das
 * war in `saas-invoice-api-frontend` nach dem Bump auf core-frontend 3.77.1
 * ein paar Stunden live der Fall.
 */
export function useCanInviteMembers(): boolean {
  return useIsOrgAdminRole()
}

/**
 * Darf der angemeldete Nutzer Konten **aktivieren/deaktivieren**?
 * (`members.activate` / `members.deactivate`)
 *
 * Dieselbe Grenze wie beim Einladen, nur an anderen Prozeduren — die
 * Mitglieder-Seiten sind bereits ab `members:member:read` erreichbar.
 */
export function useCanManageMemberStatus(): boolean {
  return useIsOrgAdminRole()
}
